import { fg } from '@atlaskit/platform-feature-flags';
import type { A2UIMessage } from '@atlassian/a2ui-renderer/types/A2UIProtocol.types';
import {
	isHumanMessage,
	isStreamResponse,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { createStoreAssistantWithContent } from './createStoreAssistantWithContent';
import { createStoreHumanWithContent } from './createStoreHumanWithContent';
import { extractA2UIFromStructuredContent } from './extractA2UIFromStructuredContent';
import { extractActionableFollowUpsMarkdownData } from './extractActionableFollowUpsMarkdownData';
import { extractMarkdownContent } from './extractMarkdownContent';
import { normalizeFinalizedMessagePresentation } from './normalizeFinalizedMessagePresentation';
import { preprocessStreamResponseDetails } from './preprocessStreamResponseDetails';
import type { ChatStoreContainerProps } from './types';

/**
 * Processes raw chat messages from the API into StoreChatItem[] with boundary content extraction.
 * Shared by fetchConversation and warmConversationCache.
 */
export const processConversationMessages = ({
	latestMessages,
	conversationId,
	sendAnalyticsEvent,
	shouldPreprocessDetails = false,
}: {
	latestMessages: Awaited<ReturnType<ChatStoreContainerProps['assistanceService']['getChat']>>;
	conversationId: string;
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	/**
	 * When true, rewrite HTML `<details><summary>` blocks into the Rovo-native
	 * `▞…▚` boundary before the expand-boundary extractor runs. Mirrors the
	 * streaming/final-response paths so history-loaded messages render the same
	 * way. Callers gate this on `shouldPreprocessDetailsForConversation(...)`.
	 */
	shouldPreprocessDetails?: boolean;
}): StoreChatItem[] => {
	const messages: StoreChatItem[] = [];

	for (const message of latestMessages) {
		if (isHumanMessage(message)) {
			// Human messages should only have application/json+a2ui if they have sent an action
			// we skip this message as there is nothing to render but this is persisted for flexibility
			// there should be no cases where a human renders A2UI from their messages
			if (
				typeof message.content === 'object' &&
				message.content !== null &&
				'data_parts_mime_type' in message.content &&
				message.content.data_parts_mime_type === 'application/json+a2ui' &&
				fg('jira_ai_rendering_a2ui')
			) {
				continue;
			}
			messages.push(createStoreHumanWithContent(message));
		} else if (isStreamResponse(message)) {
			if (fg('rovo_chat_history_lift')) {
				const normalizedPresentation = normalizeFinalizedMessagePresentation({
					message,
					options: {
						shouldExtractA2UI: fg('jira_ai_rendering_a2ui'),
						shouldExtractActionableFollowUps: fg('rovo_chat_actionable_followups_enabled'),
						shouldExtractDataSculptor: fg('rovo_enable_data_sculptor'),
						shouldExtractInlineLumina: fg('rovo_chat_enable_inline_lumina'),
						shouldExtractLegacyChartBoundaries: true,
						shouldPreprocessDetails,
					},
				});

				if (normalizedPresentation.extractionCounts.legacyChart > 0) {
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'extracted',
						actionSubject: 'vizPluginChartNonUnicode',
						attributes: {
							messageId: message.message.message.id.toString(),
							conversationId,
							chartCount: normalizedPresentation.extractionCounts.legacyChart,
							timestamp: new Date().toISOString(),
						},
					});
				}

				const storeAssistant = createStoreAssistantWithContent(normalizedPresentation.message);
				if (Object.keys(normalizedPresentation.markdownData).length > 0) {
					storeAssistant.markdownData = normalizedPresentation.markdownData;
				}

				storeAssistant.isHistorical = true;

				if (normalizedPresentation.agenticUiMessages) {
					storeAssistant.agenticUiMessages = normalizedPresentation.agenticUiMessages;
				}

				messages.push(storeAssistant);
			} else {
				// Process boundary content for historical assistant messages
				let processedMessage = message;
				const markdownData: Record<string, string> = {};

				// Rewrite HTML `<details><summary>` blocks into the Rovo-native `▞…▚`
				// boundary before the existing extractor runs so historical messages
				// render identically to their streamed form. Must happen BEFORE the
				// expand extractor below (which consumes `▞…▚`).
				if (shouldPreprocessDetails) {
					processedMessage = preprocessStreamResponseDetails(processedMessage);
				}

				// Apply the same boundary content extraction logic as streaming messages
				if (fg('rovo_enable_data_sculptor')) {
					const sculptorMsg = extractMarkdownContent({
						streamMessage: processedMessage,
						boundaryStart: '[^sculptor:start^]',
						boundaryEnd: '[^sculptor:end^]',
						replacementContent: '[^sculptor^]',
					});
					processedMessage = sculptorMsg.processedStreamMessage as StreamResponse;
					sculptorMsg.boundaryContents.forEach((content, index) => {
						markdownData[`sculptor-${index}`] = content;
					});
				}

				const expandOpenTag = '▞';
				const expandCloseTag = '▚';
				const expandPlaceholder = '[^expand^]';

				const expandMsg = extractMarkdownContent({
					streamMessage: processedMessage,
					boundaryStart: expandOpenTag,
					boundaryEnd: expandCloseTag,
					replacementContent: expandPlaceholder,
				});
				processedMessage = expandMsg.processedStreamMessage as StreamResponse;
				expandMsg.boundaryContents.forEach((content, index) => {
					markdownData[`expand-${index}`] = content;
				});

				const chartPlaceholder = '[^chart^]';
				// When processing conversation history, process both unicode and non-unicode tags
				// to handle previous conversations that used the non-unicode tag
				// Once di_chat_chart_tag_unicode is rolled out, chartMsg can be removed after 30 days
				let chartIndex = 0;

				const chartMsg = extractMarkdownContent({
					streamMessage: processedMessage,
					boundaryStart: '[^chart:start^]',
					boundaryEnd: '[^chart:end^]',
					replacementContent: chartPlaceholder,
				});
				processedMessage = chartMsg.processedStreamMessage as StreamResponse;
				chartMsg.boundaryContents.forEach((content) => {
					markdownData[`chart-${chartIndex}`] = content;
					chartIndex++;
				});

				if (chartMsg?.boundaryContents && chartMsg.boundaryContents.length > 0) {
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'extracted',
						actionSubject: 'vizPluginChartNonUnicode',
						attributes: {
							messageId: message?.message?.message?.id.toString(),
							conversationId,
							chartCount: chartMsg.boundaryContents.length,
							timestamp: new Date().toISOString(),
						},
					});
				}

				const unicodeChartMsg = extractMarkdownContent({
					streamMessage: processedMessage,
					boundaryStart: '◴',
					boundaryEnd: '◶',
					replacementContent: chartPlaceholder,
				});
				processedMessage = unicodeChartMsg.processedStreamMessage as StreamResponse;
				unicodeChartMsg.boundaryContents.forEach((content) => {
					markdownData[`chart-${chartIndex}`] = content;
					chartIndex++;
				});

				if (fg('rovo_chat_enable_inline_lumina')) {
					const luminaMsg = extractMarkdownContent({
						streamMessage: processedMessage,
						boundaryStart: '◐',
						boundaryEnd: '◑',
						replacementContent: '\n\n[^lumina^]\n\n',
					});
					processedMessage = luminaMsg.processedStreamMessage as StreamResponse;
					luminaMsg.boundaryContents.forEach((content, index) => {
						markdownData[`lumina-${index}`] = content;
					});
				}

				// Extract actionable follow-ups so history-loaded messages render the
				// same as their streamed form. Without this, the raw `◈<followup>…`
				// marker renders literally when reopening a past conversation. No-ops
				// when the experiment is in the `control` cohort.
				processedMessage = extractActionableFollowUpsMarkdownData({
					streamMessage: processedMessage,
					markdownData,
				});

				// When the backend returns structured content (A2UI messages), normalize
				// it back to a plain text string and extract data_parts for A2UI rendering.
				let extractedA2UIMessages: A2UIMessage[] | undefined;
				if (fg('jira_ai_rendering_a2ui')) {
					const a2uiResult = extractA2UIFromStructuredContent(
						processedMessage.message?.message?.content,
					);
					if (a2uiResult) {
						processedMessage.message.message.content = a2uiResult.textContent;
						extractedA2UIMessages = a2uiResult.a2uiMessages;
					}
				}

				// Create StoreAssistant with processed boundary data
				const storeAssistant = createStoreAssistantWithContent(processedMessage);
				if (Object.keys(markdownData).length > 0) {
					storeAssistant.markdownData = markdownData;
				}

				// Mark message as historical since it's loaded from conversation history
				storeAssistant.isHistorical = true;

				if (extractedA2UIMessages) {
					storeAssistant.agenticUiMessages = extractedA2UIMessages;
				}

				messages.push(storeAssistant);
			}
		}
	}

	return messages;
};
